import React, { useState, useEffect } from 'react';

const pillars = [
  {
    icon: 'fa-book',
    title: 'Grounded in Real Knowledge',
    description: 'Answers come only from human-verified FAQ and business data. If knowledge is missing, the agent acknowledges the gap instead of guessing.',
    proof: 'FAQ ingestion with source attribution',
  },
  {
    icon: 'fa-shield',
    title: 'Tested Before Deployment',
    description: 'Automated regression testing runs real voice calls and scenario simulations against your agent. Checkpoint validation catches issues before customers do.',
    proof: 'Automated voice call testing with checkpoints',
  },
  {
    icon: 'fa-search',
    title: 'Gaps Detected Automatically',
    description: 'The FAQ Optimizer analyzes real conversations to find questions your agent couldn\'t answer confidently. It generates action items so you fix gaps before they become problems.',
    proof: 'AI-powered content gap analysis',
  },
];

const StatusBadge = () => {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    fetch('https://status.remote-assistant.io/api/status.json')
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setStatus(data.status); })
      .catch(() => {});
  }, []);

  if (!status) return null;

  const colors = {
    operational: { bg: 'bg-green-500/10', border: 'border-green-500/20', dot: 'bg-green-400', text: 'text-green-400' },
    degraded: { bg: 'bg-yellow-500/10', border: 'border-yellow-500/20', dot: 'bg-yellow-400', text: 'text-yellow-400' },
    outage: { bg: 'bg-red-500/10', border: 'border-red-500/20', dot: 'bg-red-400', text: 'text-red-400' },
  };
  const labels = { operational: 'All Systems Operational', degraded: 'Partial Degradation', outage: 'System Outage' };
  const c = colors[status] || colors.operational;

  return (
    <a href="https://status.remote-assistant.io" target="_blank" rel="noopener noreferrer"
      className={`inline-flex items-center gap-2 px-3 py-1.5 ${c.bg} border ${c.border} rounded-full hover:opacity-80 transition-opacity no-underline`}>
      <span className={`w-2 h-2 rounded-full ${c.dot} ${status === 'operational' ? 'animate-pulse' : ''}`}></span>
      <span className={`${c.text} text-xs font-semibold font-['Inter']`}>{labels[status]}</span>
    </a>
  );
};

const TrustSection = () => {
  return (
    <div className="px-8 py-20 bg-neutral-900">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-500/10 border border-green-500/20 rounded-full mb-4">
            <i className="fa fa-check-circle text-green-400 text-xs"></i>
            <span className="text-green-400 text-xs font-bold font-['Inter'] tracking-wider uppercase">Reliability</span>
          </div>
          <h2 className="!text-white text-3xl lg:text-4xl font-bold font-['Montserrat'] mb-4">
            AI That Won't Hallucinate
          </h2>
          <p className="!text-stone-300 text-lg font-medium font-['Inter'] max-w-2xl mx-auto">
            Your agent won't make things up — and we prove it before it goes live.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {pillars.map((pillar) => (
            <div key={pillar.title} className="bg-neutral-800 border border-neutral-700 rounded-lg p-6 flex flex-col gap-4">
              <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center">
                <i className={`fa ${pillar.icon} text-green-400 text-xl`}></i>
              </div>
              <h3 className="!text-white text-lg font-bold font-['Montserrat']">{pillar.title}</h3>
              <p className="!text-stone-300 text-sm font-['Inter'] leading-relaxed flex-1">{pillar.description}</p>
              <div className="flex items-center gap-2 pt-2 border-t border-neutral-700">
                <i className="fa fa-check text-green-400 text-xs"></i>
                <span className="text-green-400 text-xs font-bold font-['Inter']">{pillar.proof}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-10">
          <StatusBadge />
        </div>
      </div>
    </div>
  );
};

export default TrustSection;
